import { createFileRoute } from "@tanstack/react-router";
import { useEffect } from "react";

import { SiteLayout } from "@/components/SiteLayout";
import { PageHero } from "@/components/PageHero";
import { ElfsightReviews } from "@/components/ElfsightReviews";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { CLIENT_REVIEWS, aggregateRatingFromReviews } from "@/lib/clientReviews";
import { buildBreadcrumbScript } from "@/lib/serviceHubSchema";

const SITE_URL = "https://coloradowebimpressions.com";
const BUSINESS_ID = `${SITE_URL}/#organization`;
const AGG = aggregateRatingFromReviews(CLIENT_REVIEWS);

const faqs = [
  {
    q: "Are these Google reviews real?",
    a: "Yes — every featured review on this page is verbatim from a real Colorado client on our verified Google Business Profile. The live widget below pulls additional reviews directly from Google in real time.",
  },
  {
    q: "Where are most of your clients located?",
    a: "We work exclusively with Colorado businesses, with the majority of our clients in Colorado Springs, Monument, Castle Rock, Denver, and Pueblo.",
  },
  {
    q: "What kinds of businesses leave you reviews?",
    a: "Mostly local home-service companies (roofing, HVAC, plumbing, landscaping, painting), professional services, and local retail — the same industries we specialize in.",
  },
  {
    q: "How do I leave a review?",
    a: "Just search 'Colorado Web Impressions' on Google and click 'Write a review' on our Business Profile. We read every single one.",
  },
  {
    q: "What if I had a bad experience?",
    a: "Call the owner directly at (719) 419-3935. We'd rather hear it from you first and make it right than read it on Google.",
  },
];

export const Route = createFileRoute("/reviews")({
  head: () => ({
    meta: [
        { name: "description", content: "Real reviews from Colorado Springs and Front Range clients of Colorado Web Impressions. Web design, SEO, and digital marketing testimonials." },
      {
        title: "Client Reviews | Colorado Web Impressions",
      },
      {
        name: "description",
        content: `Read ${AGG.ratingCount}+ verified Google reviews (${AGG.ratingValue}★ avg) from Colorado business owners who trust Colorado Web Impressions for web design, SEO, Google Maps, and PPC.`,
      },
      {
        property: "og:title",
        content: "Client Reviews | Colorado Web Impressions",
      },
      { property: "og:url", content: "https://coloradowebimpressions.com/reviews" },
      {
        property: "og:description",
        content: `${AGG.ratingValue}★ from ${AGG.ratingCount}+ verified Colorado clients — see what business owners are saying about working with us.`,
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: "Client Reviews | Colorado Web Impressions" },
    ],
    links: [{ rel: "canonical", href: `${SITE_URL}/reviews` }],
    scripts: [
      // LocalBusiness with AggregateRating + embedded Review array
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "LocalBusiness",
          "@id": BUSINESS_ID,
          name: "Colorado Web Impressions",
          url: SITE_URL,
          telephone: "+1-719-419-3935",
          email: "info@coloradowebimpressions.com",
          image: `${SITE_URL}/favicon.ico`,
          priceRange: "$$",
          address: {
            "@type": "PostalAddress",
            addressLocality: "Colorado Springs",
            addressRegion: "CO",
            addressCountry: "US",
          },
          areaServed: { "@type": "State", name: "Colorado" },
          aggregateRating: {
            "@type": "AggregateRating",
            ratingValue: AGG.ratingValue,
            reviewCount: AGG.ratingCount,
            bestRating: AGG.bestRating,
            worstRating: AGG.worstRating,
          },
          review: CLIENT_REVIEWS.map((r) => ({
            "@type": "Review",
            author: { "@type": "Person", name: r.author },
            datePublished: r.datePublished,
            reviewBody: r.body,
            reviewRating: {
              "@type": "Rating",
              ratingValue: r.rating,
              bestRating: 5,
              worstRating: 1,
            },
            itemReviewed: { "@id": BUSINESS_ID },
          })),
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: faqs.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        }),
      },
      buildBreadcrumbScript([
        { name: "Home", path: "/" },
        { name: "Reviews", path: "/reviews" },
      ]),
    ],
  }),
  component: ReviewsPage,
});


function ReviewsPage() {
  useEffect(() => {
    if (document.querySelector('script[src="https://elfsightcdn.com/platform.js"]')) return;
    const s = document.createElement("script");
    s.src = "https://elfsightcdn.com/platform.js";
    s.async = true;
    document.body.appendChild(s);
  }, []);

  return (
    <SiteLayout>
      <PageHero
        eyebrow="Colorado Web Impressions Reviews"
        title={
          <>
            What Colorado Business Owners <span className="text-brand-gold">Say About Us</span>
          </>
        }
        description="Verified Google reviews from real clients across Colorado Springs and the Front Range — no cherry-picking, no edits."
      />

      {/* Curated review grid + aggregate banner removed — Elfsight widget below
          is the single source of truth for displayed reviews. The structured
          data above (LocalBusiness + Review) still embeds the verified review
          corpus for crawlers and AI tools. */}

      {/* Live Google reviews widget (Elfsight) */}
      <section className="border-t border-border bg-section-alt py-16 lg:py-20">
        <div className="mx-auto max-w-6xl px-5 lg:px-8">
          <div className="mb-10 text-center">
            <h2 className="text-3xl font-extrabold tracking-tight text-foreground sm:text-4xl">
              Live Google Reviews
            </h2>
            <p className="mx-auto mt-3 max-w-2xl text-muted-foreground">
              Pulled directly from our Google Business Profile and updated in real time.
            </p>
          </div>

          <div
            className="elfsight-app-c42ab784-4775-4fde-8869-022cd088d5fe"
            data-elfsight-app-lazy
          />
        </div>
      </section>

      <section className="bg-background py-16 lg:py-24">
        <div className="mx-auto max-w-3xl px-5 lg:px-8">
          <div className="mb-10 text-center">
            <span className="inline-block rounded-full border border-brand-gold/40 bg-brand-gold/10 px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.15em] text-brand-gold-deep">
              FAQ
            </span>
            <h2 className="mt-4 text-3xl font-extrabold tracking-tight text-foreground sm:text-4xl">
              Reviews — Frequently Asked Questions
            </h2>
          </div>

          <Accordion type="single" collapsible className="w-full">
            {faqs.map((f, i) => (
              <AccordionItem key={i} value={`item-${i}`}>
                <AccordionTrigger className="text-left text-base font-semibold">
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      <ElfsightReviews
        eyebrow="Live Google reviews"
        heading="Straight from our Google Business Profile"
        description="Real, verified reviews from Colorado clients — pulled live from Google so you see them exactly as they were posted."
        className="bg-muted/30 py-16 lg:py-24"
      />
    </SiteLayout>
  );
}
